import { z } from 'zod';

/**
 * Provider from EHR.
 */
export const ehrProviderSchema = z.object({
  ehrProviderId: z.string(),
  npi: z.string().optional(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string().optional(),
  credentials: z.string().optional(), // e.g., "MD", "DO", "NP"
  specialty: z.string().optional(),
  subspecialty: z.string().optional(),
  /** Name of the provider's home location in the EHR (athena: `homedepartment`), not an id. */
  department: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
  acceptingNewPatients: z.boolean().optional(),
  languages: z.array(z.string()).optional(),
  gender: z.enum(['male', 'female', 'other']).optional(),
  photoUrl: z.string().optional(),
  bio: z.string().optional(),
});

export type EhrProvider = z.infer<typeof ehrProviderSchema>;

/**
 * Location from EHR.
 */
export const ehrLocationSchema = z.object({
  ehrLocationId: z.string(),
  name: z.string(),
  address: z.object({
    line1: z.string().optional(),
    line2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
    country: z.string().optional(),
  }),
  phone: z.string().optional(),
  fax: z.string().optional(),
  timezone: z.string().optional(), // IANA timezone (e.g., "America/New_York")
  /**
   * EHR ids of the providers who practice at this location, when the EHR reports
   * it (athena: `departments?providerlist=true`). Absent means the connector does
   * not report provider links; either way the API only ever ADDS links from it
   * (ADR-261), so absent and empty change nothing.
   */
  ehrProviderIds: z.array(z.string()).optional(),
  coordinates: z
    .object({
      latitude: z.number(),
      longitude: z.number(),
    })
    .optional(),
  hoursOfOperation: z
    .array(
      z.object({
        dayOfWeek: z.enum([
          'monday',
          'tuesday',
          'wednesday',
          'thursday',
          'friday',
          'saturday',
          'sunday',
        ]),
        openTime: z.string(), // HH:MM format
        closeTime: z.string(), // HH:MM format
      })
    )
    .optional(),
});

export type EhrLocation = z.infer<typeof ehrLocationSchema>;

/**
 * Time slot from EHR.
 */
export const ehrTimeSlotSchema = z.object({
  slotId: z.string(),
  startTime: z.string(), // ISO 8601 datetime
  endTime: z.string(), // ISO 8601 datetime
  durationMinutes: z.number(),
  status: z.enum(['free', 'busy', 'tentative']),
  ehrProviderId: z.string(),
  ehrLocationId: z.string(),
  appointmentTypes: z.array(z.string()).optional(), // Types of appointments this slot can accommodate
});

export type EhrTimeSlot = z.infer<typeof ehrTimeSlotSchema>;

/**
 * Provider availability response.
 */
export const ehrProviderAvailabilitySchema = z.object({
  ehrProviderId: z.string(),
  ehrLocationId: z.string(),
  slots: z.array(ehrTimeSlotSchema),
  fetchedAt: z.string(), // ISO 8601 datetime
});

export type EhrProviderAvailability = z.infer<typeof ehrProviderAvailabilitySchema>;
